/**
 * Turns a failed API call into low-cardinality Sentry tags.
 *
 * No imports on purpose — tested by importing this file directly under
 * Node's TypeScript stripping, which has no module resolution.
 */

/** Sentry drops tag values longer than 200 characters. */
export const TAG_VALUE_MAX_LENGTH = 200;

/** Params that vary per call but don't identify the endpoint. */
const VOLATILE_QUERY_PARAMS = ["limit", "offset", "page", "cursor", "sort", "sortBy", "order", "orderBy"];

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LONG_NUMERIC_SEGMENT = /^\d{4,}$/;

/**
 * Collapses id-shaped path segments (UUIDs, long numeric ids) to `:id`.
 *
 * @param path - Request path, without query string
 * @returns Path with id-like segments replaced
 */
const normalizePath = (path: string): string =>
  path
    .split("/")
    .map((segment) => (UUID_SEGMENT.test(segment) || LONG_NUMERIC_SEGMENT.test(segment) ? ":id" : segment))
    .join("/");

/** Placeholder for any query value; kept distinct from `:id` since not every value is an identifier. */
const QUERY_VALUE_PLACEHOLDER = ":value";

/**
 * Drops volatile params, replaces every remaining value with `:value`.
 *
 * @param query - Query string, without the leading `?`
 * @returns Normalized query string
 */
const normalizeQuery = (query: string): string => {
  const params = new URLSearchParams(query);
  VOLATILE_QUERY_PARAMS.forEach((param) => params.delete(param));
  // Built manually, not via toString(), to keep ":" literal instead of percent-encoded.
  const keys = Array.from(new Set(params.keys())).sort();
  return keys.map((key) => `${key}=${QUERY_VALUE_PLACEHOLDER}`).join("&");
};

/**
 * Normalizes a request URL into a stable `endpoint` tag.
 *
 * @param url - Request URL, absolute or relative
 * @returns Normalized tag value, capped at {@link TAG_VALUE_MAX_LENGTH}
 */
export const normalizeEndpoint = (url?: string): string => {
  if (!url) {
    return "unknown";
  }
  const separatorIndex = url.indexOf("?");
  const path = normalizePath(separatorIndex === -1 ? url : url.slice(0, separatorIndex));
  const query = separatorIndex === -1 ? "" : normalizeQuery(url.slice(separatorIndex + 1));
  return (query ? `${path}?${query}` : path).slice(0, TAG_VALUE_MAX_LENGTH);
};

/**
 * Buckets an error status into a `statusClass` tag.
 *
 * @param statusCode - HTTP status code or RTK Query status string
 * @returns `4xx`/`5xx`, the RTK status string, or `undefined`
 */
export const getStatusClass = (statusCode: unknown): string | undefined => {
  if (typeof statusCode === "number" && Number.isFinite(statusCode)) {
    return `${Math.floor(statusCode / 100)}xx`;
  }
  if (typeof statusCode === "string") {
    const trimmed = statusCode.trim();
    if (trimmed) {
      return trimmed.slice(0, TAG_VALUE_MAX_LENGTH);
    }
  }
  return undefined;
};
